import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ventasAPI, lotesAPI } from '../lib/api';
import { formatNumber, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Receipt, Lightning, Trash, Plus, ClipboardText, Check } from '@phosphor-icons/react';

export default function VentaRapida() {
  const navigate = useNavigate();
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLotes, setLoadingLotes] = useState(true);
  
  // Global settings
  const [loteId, setLoteId] = useState('');
  const [precioLibra, setPrecioLibra] = useState('48.00');
  
  // Rows data
  const [rows, setRows] = useState([createEmptyRow()]);
  
  // Paste mode
  const [pasteData, setPasteData] = useState('');
  const [showPasteMode, setShowPasteMode] = useState(false);

  function createEmptyRow() {
    return {
      id: Date.now() + Math.random(),
      no_conduce: '',
      cliente: '',
      fecha: new Date().toISOString().split('T')[0],
      total_jaulas: '',
      cantidad_pollos: '',
      peso_tara: '',
      peso_bruto: '',
      precio: precioLibra, // Precio por fila, usa el global como default
      dias_credito: '1', // Días de crédito por fila
    };
  }

  useEffect(() => {
    loadLotes();
  }, []);

  const loadLotes = async () => {
    try {
      const response = await lotesAPI.getAll('activo');
      setLotes(response.data);
      if (response.data.length === 1) {
        setLoteId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Error al cargar los lotes');
    } finally {
      setLoadingLotes(false);
    }
  };

  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const addRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const removeRow = (index) => {
    if (rows.length > 1) {
      setRows(rows.filter((_, i) => i !== index));
    }
  };

  const calculateRowTotals = (row) => {
    const pesoNeto = (parseFloat(row.peso_bruto) || 0) - (parseFloat(row.peso_tara) || 0);
    const pesoPromedio = row.cantidad_pollos ? pesoNeto / parseFloat(row.cantidad_pollos) : 0;
    // Usar precio de la fila, o el global si no tiene
    const precioFila = parseFloat(row.precio) || parseFloat(precioLibra) || 0;
    const total = pesoNeto * precioFila;
    return { pesoNeto, pesoPromedio, total, precioFila };
  };

  const handlePasteData = () => {
    if (!pasteData.trim()) {
      toast.error('No hay datos para procesar');
      return;
    }

    try {
      const lines = pasteData.trim().split('\n');
      const newRows = [];

      for (const line of lines) {
        // Split by tab (Excel default) or multiple spaces
        const cols = line.split(/\t|  +/).map(c => c.trim()).filter(c => c);
        
        if (cols.length >= 6) {
          // Expected order: No.Conduce, Cliente, Fecha, Jaulas, Pollos, PesoTara, PesoBruto, Precio
          // Or without date: No.Conduce, Cliente, Jaulas, Pollos, PesoTara, PesoBruto, Precio
          let noConduce, cliente, fecha, jaulas, pollos, pesoTara, pesoBruto, precio;
          
          if (cols.length >= 8) {
            // Con fecha y precio
            [noConduce, cliente, fecha, jaulas, pollos, pesoTara, pesoBruto, precio] = cols;
          } else if (cols.length === 7) {
            // Puede ser con fecha sin precio, o sin fecha con precio
            // Verificamos si el tercer elemento parece una fecha
            if (cols[2].includes('/') || cols[2].includes('-') || /^\d{4}/.test(cols[2])) {
              // Con fecha, sin precio
              [noConduce, cliente, fecha, jaulas, pollos, pesoTara, pesoBruto] = cols;
              precio = precioLibra;
            } else {
              // Sin fecha, con precio
              [noConduce, cliente, jaulas, pollos, pesoTara, pesoBruto, precio] = cols;
              fecha = new Date().toISOString().split('T')[0];
            }
          } else {
            // Sin fecha ni precio
            [noConduce, cliente, jaulas, pollos, pesoTara, pesoBruto] = cols;
            fecha = new Date().toISOString().split('T')[0];
            precio = precioLibra;
          }

          // Parse date - INPUT format is ALWAYS MM/DD/YYYY (American format)
          // Example: 03/10/2026 = March 10, 2026
          if (fecha && fecha.includes('/')) {
            // Remove time part if present (e.g., "03/10/2026 8:10 p. m." -> "03/10/2026")
            let fechaClean = fecha.split(' ')[0]; // Take only date part before space
            
            const parts = fechaClean.split('/');
            if (parts.length === 3) {
              let [month, day, year] = parts;
              
              // Handle year with 2 or 4 digits
              if (year.length === 2) {
                year = '20' + year; // Assume 2000s
              }
              
              // Convert MM/DD/YYYY to YYYY-MM-DD (ISO format)
              fecha = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
          }

          // Clean price value (remove $ and commas)
          const precioClean = precio?.toString().replace(/[$,]/g, '') || precioLibra;

          newRows.push({
            id: Date.now() + Math.random(),
            no_conduce: noConduce || '',
            cliente: cliente || '',
            fecha: fecha || new Date().toISOString().split('T')[0],
            total_jaulas: jaulas?.replace(/,/g, '') || '',
            cantidad_pollos: pollos?.replace(/,/g, '') || '',
            peso_tara: pesoTara?.replace(/,/g, '') || '',
            peso_bruto: pesoBruto?.replace(/,/g, '') || '',
            precio: precioClean,
          });
        }
      }

      if (newRows.length > 0) {
        setRows(newRows);
        setPasteData('');
        setShowPasteMode(false);
        toast.success(`${newRows.length} filas importadas correctamente`);
      } else {
        toast.error('No se pudieron procesar los datos. Verifique el formato.');
      }
    } catch (error) {
      // Error silenciado
      toast.error('Error al procesar los datos');
    }
  };

  const handleSubmitAll = async () => {
    if (!loteId) {
      toast.error('Seleccione un lote');
      return;
    }

    // Validate rows
    const validRows = rows.filter(row => 
      row.no_conduce && row.cliente && row.cantidad_pollos && row.peso_tara && row.peso_bruto
    );

    if (validRows.length === 0) {
      toast.error('Complete al menos una fila con los datos requeridos');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const row of validRows) {
      try {
        // Usar precio de la fila, o el global si no tiene
        const precioFila = parseFloat(row.precio) || parseFloat(precioLibra) || 0;
        
        const payload = {
          lote_id: loteId,
          no_conduce: row.no_conduce,
          cliente: row.cliente,
          fecha: row.fecha.includes('T') ? row.fecha : `${row.fecha}T12:00`,
          total_jaulas: parseInt(row.total_jaulas) || 0,
          cantidad_pollos: parseInt(row.cantidad_pollos),
          peso_tara: parseFloat(row.peso_tara),
          peso_bruto: parseFloat(row.peso_bruto),
          precio_libra: precioFila,
          dias_credito: parseInt(row.dias_credito) || 0,
        };

        await ventasAPI.create(payload);
        successCount++;
      } catch (error) {
        // Error silenciado
        errorCount++;
      }
    }

    setLoading(false);

    if (successCount > 0) {
      toast.success(`${successCount} venta(s) registrada(s) correctamente`);
      if (errorCount === 0) {
        navigate('/ventas');
      }
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} venta(s) fallaron al registrar`);
    }
  };

  // Calculate totals
  const grandTotal = rows.reduce((sum, row) => {
    const { total } = calculateRowTotals(row);
    return sum + total;
  }, 0);

  const totalPollos = rows.reduce((sum, row) => sum + (parseInt(row.cantidad_pollos) || 0), 0);
  const totalPesoNeto = rows.reduce((sum, row) => {
    const { pesoNeto } = calculateRowTotals(row);
    return sum + pesoNeto;
  }, 0);

  const selectedLote = lotes.find(l => l.id === loteId);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="venta-rapida">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <Link
              to="/ventas"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
            >
              <ArrowLeft size={14} />
              Volver a Ventas
            </Link>
            <h1 className="text-3xl font-bold tracking-tight uppercase flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Lightning size={32} className="text-[#F69031]" weight="fill" />
              Entrada Rápida de Ventas
            </h1>
            <p className="text-muted-foreground">
              Registre múltiples ventas copiando y pegando desde Excel
            </p>
          </div>
        </div>

        {/* Global Settings */}
        <Card className="border-border/60 bg-gradient-to-r from-red-50 to-orange-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Configuración Global</CardTitle>
            <CardDescription>
              Estos valores se aplicarán a todas las ventas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lote *</Label>
                <Select value={loteId} onValueChange={setLoteId}>
                  <SelectTrigger data-testid="select-lote-global">
                    <SelectValue placeholder="Seleccione lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {lotes.map((lote) => (
                      <SelectItem key={lote.id} value={lote.id}>
                        {lote.nombre} - Día {lote.dias_transcurridos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Precio por Defecto ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={precioLibra}
                  onChange={(e) => setPrecioLibra(e.target.value)}
                  data-testid="precio-global"
                />
                <p className="text-xs text-muted-foreground">Se usa si no se especifica en los datos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paste Mode Toggle */}
        <div className="flex gap-2">
          <Button
            variant={showPasteMode ? "default" : "outline"}
            onClick={() => setShowPasteMode(!showPasteMode)}
            className={showPasteMode ? "bg-[#E73425] hover:bg-[#D81C23]" : ""}
          >
            <ClipboardText size={18} className="mr-2" />
            {showPasteMode ? 'Ocultar Área de Pegado' : 'Pegar desde Excel'}
          </Button>
          <Button variant="outline" onClick={addRow}>
            <Plus size={18} className="mr-2" />
            Agregar Fila
          </Button>
          {selectedLote && (
            <div className="flex items-center px-3 bg-muted rounded-md text-sm font-medium">
              Lote: <span className="text-[#E73425] ml-1">{selectedLote.nombre}</span>
            </div>
          )}
        </div>

        {/* Paste Area */}
        {showPasteMode && (
          <Card className="border-border/60 border-dashed border-2 border-[#E73425]/50">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-semibold">Pegue los datos desde Excel</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Formato esperado (columnas separadas por TAB): <br/>
                    <code className="bg-muted px-2 py-1 rounded text-xs">
                      No.Conduce | Cliente | Fecha (MM/DD/AAAA) | Jaulas | Pollos | PesoTara | PesoBruto | Precio
                    </code>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    * Fecha en formato MES/DIA/AÑO (ej: 03/10/2026 = 10 de Marzo). El precio es opcional.
                  </p>
                </div>
                <Textarea
                  placeholder="Pegue aquí los datos copiados de Excel (Ctrl+V)..."
                  value={pasteData}
                  onChange={(e) => setPasteData(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                  data-testid="paste-area"
                />
                <Button 
                  onClick={handlePasteData}
                  className="bg-[#E73425] hover:bg-[#D81C23]"
                  disabled={!pasteData.trim()}
                >
                  <Check size={18} className="mr-2" />
                  Procesar Datos
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Table */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt size={24} className="text-[#F69031]" />
              Datos de Ventas ({rows.length} fila{rows.length !== 1 ? 's' : ''})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-2 text-left font-semibold w-20">No. Cond.</th>
                    <th className="p-2 text-left font-semibold">Cliente</th>
                    <th className="p-2 text-left font-semibold w-28">Fecha</th>
                    <th className="p-2 text-right font-semibold w-16">Jaulas</th>
                    <th className="p-2 text-right font-semibold w-20">Pollos</th>
                    <th className="p-2 text-right font-semibold w-24">P. Tara</th>
                    <th className="p-2 text-right font-semibold w-24">P. Bruto</th>
                    <th className="p-2 text-right font-semibold w-24 bg-emerald-50">P. Neto</th>
                    <th className="p-2 text-right font-semibold w-16 bg-blue-50">Prom.</th>
                    <th className="p-2 text-right font-semibold w-20 bg-orange-50">Precio</th>
                    <th className="p-2 text-right font-semibold w-28 bg-amber-50">Total</th>
                    <th className="p-2 text-center font-semibold w-20 bg-purple-50">Crédito</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const { pesoNeto, pesoPromedio, total, precioFila } = calculateRowTotals(row);
                    return (
                      <tr key={row.id} className="border-b hover:bg-muted/30">
                        <td className="p-1">
                          <Input
                            value={row.no_conduce}
                            onChange={(e) => updateRow(index, 'no_conduce', e.target.value)}
                            className="h-8 text-sm"
                            placeholder="1237"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={row.cliente}
                            onChange={(e) => updateRow(index, 'cliente', e.target.value)}
                            className="h-8 text-sm"
                            placeholder="Nombre"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="date"
                            value={row.fecha}
                            onChange={(e) => updateRow(index, 'fecha', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            value={row.total_jaulas}
                            onChange={(e) => updateRow(index, 'total_jaulas', e.target.value)}
                            className="h-8 text-sm text-right"
                            placeholder="0"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            value={row.cantidad_pollos}
                            onChange={(e) => updateRow(index, 'cantidad_pollos', e.target.value)}
                            className="h-8 text-sm text-right"
                            placeholder="0"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={row.peso_tara}
                            onChange={(e) => updateRow(index, 'peso_tara', e.target.value)}
                            className="h-8 text-sm text-right"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            type="number"
                            step="0.01"
                            value={row.peso_bruto}
                            onChange={(e) => updateRow(index, 'peso_bruto', e.target.value)}
                            className="h-8 text-sm text-right"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="p-1 bg-emerald-50">
                          <div className="h-8 px-2 flex items-center justify-end font-semibold text-emerald-600">
                            {formatNumber(pesoNeto)}
                          </div>
                        </td>
                        <td className="p-1 bg-blue-50">
                          <div className="h-8 px-2 flex items-center justify-end font-medium text-blue-600">
                            {pesoPromedio.toFixed(2)}
                          </div>
                        </td>
                        <td className="p-1 bg-orange-50">
                          <Input
                            type="number"
                            step="0.01"
                            value={row.precio || ''}
                            onChange={(e) => updateRow(index, 'precio', e.target.value)}
                            className="h-8 text-sm text-right bg-orange-50"
                            placeholder={precioLibra}
                          />
                        </td>
                        <td className="p-1 bg-amber-50">
                          <div className="h-8 px-2 flex items-center justify-end font-semibold text-amber-600">
                            {formatCurrency(total)}
                          </div>
                        </td>
                        <td className="p-1 bg-purple-50">
                          <Select
                            value={row.dias_credito || '1'}
                            onValueChange={(value) => updateRow(index, 'dias_credito', value)}
                          >
                            <SelectTrigger className="h-8 text-sm bg-purple-50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">Contado</SelectItem>
                              <SelectItem value="1">1 día</SelectItem>
                              <SelectItem value="3">3 días</SelectItem>
                              <SelectItem value="7">7 días</SelectItem>
                              <SelectItem value="14">14 días</SelectItem>
                              <SelectItem value="21">21 días</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                            onClick={() => removeRow(index)}
                            disabled={rows.length === 1}
                          >
                            <Trash size={16} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/50 font-semibold">
                    <td className="p-2" colSpan={4}>TOTALES</td>
                    <td className="p-2 text-right">{formatNumber(totalPollos)}</td>
                    <td className="p-2" colSpan={2}></td>
                    <td className="p-2 text-right bg-emerald-100 text-emerald-700">{formatNumber(totalPesoNeto)}</td>
                    <td className="p-2 text-right bg-blue-100 text-blue-700">
                      {totalPollos > 0 ? (totalPesoNeto / totalPollos).toFixed(2) : '0.00'}
                    </td>
                    <td className="p-2 bg-orange-100"></td>
                    <td className="p-2 text-right bg-amber-100 text-amber-700">{formatCurrency(grandTotal)}</td>
                    <td className="p-2 bg-purple-100"></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Summary and Submit */}
        <Card className="border-border/60">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-sm text-muted-foreground">Total Pollos</p>
                  <p className="text-2xl font-bold">{formatNumber(totalPollos)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Peso Neto Total</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatNumber(totalPesoNeto)} lb</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Ventas</p>
                  <p className="text-2xl font-bold text-[#E73425]">{formatCurrency(grandTotal)}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/ventas')}>
                  Cancelar
                </Button>
                <Button
                  className="bg-[#E73425] hover:bg-[#D81C23] px-8"
                  onClick={handleSubmitAll}
                  disabled={loading || !loteId}
                  data-testid="submit-all"
                >
                  {loading ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    <>
                      <Check size={18} className="mr-2" />
                      Registrar {rows.filter(r => r.no_conduce && r.cliente).length} Venta(s)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
